import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { apiGet } from "../services/api";
import { Pencil, Save, X } from "lucide-react"; // icon for edit button
import Toast from "./Toast";
import Footer from "./Footer";

export default function Profile({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem("token");

  // Auto-clear toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await apiGet("student", token);
        if (res.error) {
          setError(res.error);
        } else {
          setStudentData(res);
        }
      } catch (err) {
        setError("Unable to fetch student data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [token]);

  const getImageUrl = () => {
    if (!studentData) return "";
    const candidateId = studentData.profile?.CandidateId;
    const photograph = studentData.profile?.photograph;

    if (candidateId) {
      return `https://pravesh.gbu.ac.in/app/userimages/${candidateId}.jpg`;
    }

    if (photograph) {
      let cleanedPath = photograph.replace(/^(\.\.\/)+/, "").replace(/^\/+/, "");
      return `https://hostels.gbu.ac.in/ohms/${cleanedPath}`;
    }

    return "";
  };

  const startEditing = (section, data) => {
    setEditingSection(section);
    setEditData({ ...data });
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Use the generic apiPost from services for updating profile
      const API_URL = "https://ohms.gbu.ac.in/api/index.php?route=";
      const response = await fetch(API_URL + "student_profile&action=updateProfile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editData),
      });

      const result = await response.json();

      if (result.success || response.ok) {
        // Update local state with new data
        setStudentData((prev) => ({
          ...prev,
          profile: {
            ...prev.profile,
            ...editData,
          },
        }));
        setToast({
          type: "success",
          message: "Profile updated successfully!",
        });
        setEditingSection(null);
        setEditData({});
      } else {
        setToast({
          type: "error",
          message: result.message || "Failed to update profile",
        });
      }
    } catch (err) {
      setToast({
        type: "error",
        message: "Error updating profile",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col md:ml-64">
        <Topbar
          setSidebarOpen={setSidebarOpen}
          enrollment={studentData?.student?.enrollment_id}
        />

        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && <p className="text-gray-500">Loading...</p>}
          {error && <p className="text-red-600">{error}</p>}
          {toast && <Toast type={toast.type} message={toast.message} />}

          {!loading && !error && studentData && (
            <>
              {/* Header */}
              <div className="bg-white rounded-xl shadow p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center space-x-6">
                  <img
                    src={getImageUrl()}
                    alt="Student Photograph"
                    className="w-28 h-28 object-cover rounded-full border-2 border-blue-200 shadow"
                    onError={(e) => {
                      e.target.src = "/placeholder-avatar.png";
                    }}
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-blue-700 mb-1">
                      {studentData.student.name}
                    </h2>
                    <p className="text-gray-600">
                      Enrollment No:{" "}
                      <span className="font-mono">
                        {studentData.student.enrollment_id}
                      </span>
                    </p>
                    <p className="text-gray-600">
                      Roll No: {studentData.student.roll_number}
                    </p>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <span
                    className={`inline-block px-3 py-1 text-sm rounded ${
                      studentData.student.document_status === "approved"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    Document Status: {studentData.student.document_status}
                  </span>
                </div>
              </div>

              {/* Profile Sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProfileCard
                  title="Personal Information"
                  sectionId="personal"
                  isEditing={editingSection === "personal"}
                  fields={[
                    { key: "Cname", label: "Name", value: studentData.profile.Cname },
                    { key: "gender", label: "Gender", value: studentData.profile.gender },
                    { key: "CDob", label: "Date of Birth", value: studentData.profile.CDob },
                    { key: "CAddress", label: "Address", value: studentData.profile.CAddress },
                    { key: "CAadhar", label: "Aadhar", value: studentData.profile.CAadhar },
                    { key: "stud_email", label: "Email", value: studentData.profile.stud_email },
                    { key: "stud_mobile", label: "Mobile", value: studentData.profile.stud_mobile },
                    { key: "FatherName", label: "Father's Name", value: studentData.profile.FatherName },
                    { key: "Fmobile", label: "Father's Mobile", value: studentData.profile.Fmobile },
                    { key: "MotherName", label: "Mother's Name", value: studentData.profile.MotherName },
                    { key: "MMobile", label: "Mother's Mobile", value: studentData.profile.MMobile },
                  ]}
                  editData={editData}
                  onEdit={(data) => startEditing("personal", data)}
                  onCancel={cancelEditing}
                  onSave={saveChanges}
                  onEditChange={handleEditChange}
                  saving={saving}
                />

                <ProfileCard
                  title="Program Information"
                  sectionId="program"
                  isEditing={editingSection === "program"}
                  fields={[
                    { key: "ProgrammeName", label: "Program", value: studentData.profile.ProgrammeName },
                    { key: "department", label: "Department", value: studentData.profile.department },
                    { key: "school", label: "School", value: studentData.profile.school }
                  ]}
                  editData={editData}
                  onEdit={(data) => startEditing("program", data)}
                  onCancel={cancelEditing}
                  onSave={saveChanges}
                  onEditChange={handleEditChange}
                  saving={saving}
                />

                <ProfileCard
                  title="Hostel Information"
                  sectionId="hostel"
                  isEditing={false}
                  fields={
                    studentData.allocation
                      ? [
                          { key: "hostel_name", label: "Hostel", value: studentData.allocation.hostel_name },
                          { key: "room_number", label: "Room Number", value: studentData.allocation.room_number },
                          { key: "floor_number", label: "Floor", value: studentData.allocation.floor_number },
                          { key: "status", label: "Status", value: studentData.allocation.status },
                          { key: "allocation_date", label: "Allocation Date", value: studentData.allocation.allocation_date },
                        ]
                      : [{ key: "allocation", label: "Hostel Allocation", value: "No current allocation" }]
                  }
                  canEdit={false}
                />
              </div>
            </>
          )}
        </main>
        {/* Footer */}
      <Footer />
      </div>
    </div>
  );
}

function ProfileCard({
  title,
  sectionId,
  fields,
  isEditing,
  editData,
  onEdit,
  onCancel,
  onSave,
  onEditChange,
  saving,
  canEdit = true,
}) {
  return (
    <div className="bg-white rounded-xl shadow hover:shadow-lg transition p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-blue-700">{title}</h3>
        {canEdit && (
          <>
            {!isEditing ? (
              <button
                type="button"
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition"
                onClick={() => onEdit(fields.reduce((acc, f) => ({ ...acc, [f.key]: f.value }), {}))}
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex items-center text-green-600 hover:text-green-800 text-sm font-medium transition"
                  onClick={onSave}
                  disabled={saving}
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium transition"
                  onClick={onCancel}
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isEditing ? (
        <div className="grid grid-cols-1 gap-3">
          {fields.map((field) => {
            const isGender = field.label === "Gender";
            const isDate = field.label === "Date of Birth";
            const isPhone = field.key.toLowerCase().includes("mobile") || field.key.toLowerCase().includes("phone");
            const isAddress = field.key.includes("Address");
            const isTextarea = isAddress;
            
            return (
              <div key={field.key} className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">{field.label}</label>
                {isGender ? (
                  <select
                    value={editData[field.key] || field.value || ""}
                    onChange={(e) => onEditChange(field.key, e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                ) : isTextarea ? (
                  <textarea
                    value={editData[field.key] !== undefined ? editData[field.key] : field.value || ""}
                    onChange={(e) => onEditChange(field.key, e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="2"
                  />
                ) : (
                  <input
                    type={isDate ? "date" : isPhone ? "tel" : "text"}
                    value={editData[field.key] !== undefined ? editData[field.key] : field.value || ""}
                    onChange={(e) => onEditChange(field.key, e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {fields.map((field) => (
            <div
              key={field.key}
              className="border border-gray-200 rounded p-3 hover:bg-gray-50 flex flex-col"
            >
              <span className="text-xs text-gray-500">{field.label}</span>
              <span className="font-medium text-gray-800">{field.value || "N/A"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
